import { FormSchema, OptionShape } from "@stormwater-software/lib-form";
import { UIControlType } from "@stormwater-software/lib-form-control-types";
import { Placement } from "popper.js";
import React, { MouseEventHandler, useContext, useEffect, useState } from "react";
import ProjectContext from "../../../contexts/ProjectContext";
import ProjectPrintContext, {
  InspectionDateFilter,
  PrintProjectFormData,
} from "../../../contexts/ProjectPrintContext";
import { InspectionIdsControl } from "../../Inspections/DateFilter/InspectionIdsControl";
import { getMapOptionsSchema } from "../../Inspections/Details/InspectionPrint";
import LoadingModal from "../../Shared/Loading/LoadingModal";
import PrintModal from "../../Shared/Print/PrintModal";
import AppContext from "../../../contexts/AppContext";
import TenantFeatureContext from "../../../contexts/TenantContext";
import projectInspectionTemplatesApi from "../../../utils/api/projectInspectionTemplates";

export type InspectionSchemaOptions = {
  inspections: OptionShape[];
  filter: InspectionDateFilter;
  filterOptions: OptionShape[];
  inspectionTextLabel: string;
};

export const getInspectionFilterSchema = ({
  inspections,
  filter,
  filterOptions,
  inspectionTextLabel,
}: InspectionSchemaOptions): FormSchema => ({
  // inspection_label: {
  //   label: "Inspections",
  //   controlType: UIControlType.plainText,
  //   style: { marginBottom: 0 },
  //   className: inspections.length === 0 ? "hidden" : "",
  // },
  timeFilter: {
    controlType: UIControlType.select,
    parse: Number,
    label: "Filter",
    options: filterOptions,
    style: { flex: "0 1 33%", paddingLeft: "2rem" },
  },
  start_date: {
    controlType: UIControlType.date,
    disabled: (filter.prevMonths || 0) > 0,
    label: "Start Date",
    style: { flex: "0 1 33%" },
    popperPlacement: "top" as Placement,
  },
  end_date: {
    controlType: UIControlType.date,
    disabled: (filter.prevMonths || 0) > 0,
    label: "End Date",
    style: { flex: "0 1 33%" },
    popperPlacement: "top" as Placement,
    validation: {
      maxDate: new Date(),
    },
  },
  inspectionText: {
    controlType: UIControlType.plainText,
    label: inspectionTextLabel,
    style: { paddingLeft: "2rem", marginBottom: "1rem" },
  },
  select_all: {
    controlType: UIControlType.checkbox,
    label: "Select All",
    style: { marginBottom: 0, paddingLeft: "2rem" },
    className:
      "inspection-select-all " + (inspections.length === 0 ? "hidden" : ""),
  },
  inspectionIds: {
    controlType: UIControlType.custom,
    renderControl: InspectionIdsControl,
    options: inspections,
    validation: {
      required: inspections.length > 0,
    },
    style: { paddingLeft: "2rem" },
    className: "form-group--checkboxGroup two-columns",
  },
});

/**
 * Client-side filter: given all inspections and a selected template type id,
 * return only inspections whose template matches that type.
 */
function filterInspectionsByFormType(
  allInspections: any[],
  selectedFormType: string | number | undefined,
): any[] {
  if (!selectedFormType) {
    return [];
  }

  console.log("allInspections", allInspections);


  const typeId = Number(selectedFormType);

  return allInspections.filter((insp: any) => {
    return insp.inspection_template?.inspection_type_id === typeId;
  });
}

const getPrintInspectionsSchema = (
  options: InspectionSchemaOptions & {
    formTypeOptions: { label: string; value: number | string }[];
    selectedFormType: string | number | undefined;
  },
) => {
  const appContext = useContext(AppContext);
  const featureContext = useContext(TenantFeatureContext);
  const user = appContext.get("user");

  const schema: FormSchema = {
    ...getMapOptionsSchema(false, true, !featureContext.tenantHasFeature("Site Map - Editor Updates"), user.roleName, true),
  };

  // Always show form type selector
  if (options.formTypeOptions.length > 0) {
    schema.inspectionFormType = {
      controlType: UIControlType.select,
      label: "Form Type*",
      options: options.formTypeOptions,
      validation: { required: true },
      placeholder: "Select a form type",
    };
  }

  // Only show inspection selection when a form type is selected
  if (options.selectedFormType) {
    console.log("i", options);

    Object.assign(schema, getInspectionFilterSchema(options));
  }

  return schema;
};

export interface IPrintInspectionsModal {
  onClose: MouseEventHandler<Element>;
}

const PrintInspectionsModal: React.FC<IPrintInspectionsModal> = ({
  onClose,
}) => {
  const projectContext = useContext(ProjectContext);
  const projectPrintContext = useContext(ProjectPrintContext);
  const [formTypeOptions, setFormTypeOptions] = useState<{ label: string; value: number | string }[]>([]);

  const loadFormTypes = async () => {
    const projectId = projectContext.project?.id;

    if (!projectId) return;

    try {
      const templateTypes = await projectInspectionTemplatesApi.getUniqueTemplateTypes(projectId);

      if (templateTypes && templateTypes.length > 0) {
        const options = templateTypes.map((t) => ({
          label: t.name,
          value: t.id,
        }));

        setFormTypeOptions(options);
      } else {
        setFormTypeOptions([]);
      }
    } catch {
      setFormTypeOptions([]);
    }
  };

  useEffect(() => {
    projectContext.getCustomPrint.refetch();
    loadFormTypes();
  }, []);

  if (!projectContext.project || projectContext.inspectionsQuery.isFetching) {
    return <LoadingModal show />;
  }

  return (
    <PrintModal
      jobName={"inspections"}
      className="print-inspections-modal"
      jobData={{
        projectId: projectContext.project.id,
      }}
      statusUpdateKey={projectContext.project.id}
      onClose={onClose}
      formSchema={(formContextValue: PrintProjectFormData) => {
        const filter: InspectionDateFilter = {
          start: formContextValue.start_date,
          end: formContextValue.end_date,
          prevMonths: formContextValue.timeFilter,
        };

        const selectedFormType = formContextValue.inspectionFormType;

        // Filter inspections client-side based on selected form type
        const allInspections = projectContext.getCustomPrint.data || [];
        const typeFilteredInspections = filterInspectionsByFormType(
          allInspections,
          selectedFormType,
        );

        const inspections = projectPrintContext.getInspectionOptions(
          typeFilteredInspections,
          filter,
        );

        return getPrintInspectionsSchema({
          inspections,
          filter,
          filterOptions: projectPrintContext.timeFilterOptions,
          inspectionTextLabel: selectedFormType
            ? projectPrintContext.getInspectionText({
              inspections,
              filter,
            })
            : "Select a form type to view inspections",
          formTypeOptions,
          selectedFormType,
        });
      }}
      initialFormData={{
        inspectionIds: {},
        include_findings: true,
        projectId: projectContext.project.id,
        timeFilter: 3,
        include_map_drawings: false,
        include_map_changelog: false,
        include_finding_attachment: false,
        include_attachment: false,
      }}
      modalTitle="Print Inspections"
      validateData={projectPrintContext.validateInspectionData}
    />
  );
};

export default PrintInspectionsModal;
